/**
 * Domain types — a near-1:1 mirror of the real backend schema (from the
 * `methane_kz_demo` PostGIS dump), so a real REST adapter is a thin mapping,
 * not a rewrite:
 *
 *   Plume         ← public.plume        (one detection)
 *   EpisodeMember ← map.episode_member  (plume ↔ episode link)
 *   Episode       ← map.episode         (a cluster of plumes)
 *   Aoi           ← public.aoi          (a facility / source zone)
 *
 * Field names and nullability match the columns. Coordinates are WGS84 (4326),
 * [lng, lat]. Timestamps are ISO strings.
 *
 * ── Verified against the live database on 2026-07-31 ──
 *  - `quantification_status`: "ok" and "no_wind" occur; "failed" is declared by
 *    the backend and kept here.
 *  - `sensor`: the only value is "sentinel-2" (see `normalizeSensor`, which
 *    also handles the Landsat and EMIT strings the ingest may add later).
 *  - `aoi.category`: "oil_gas" | "coal_mine" | "landfill", from the AOI layer
 *    the pipeline builds; mapped to sector labels in i18n/ru.ts. Note that
 *    public.aoi is empty, so AOIs arrive as category-wide buffers rather than
 *    named facilities — see `toAois` in services/api.ts.
 */
import type { Polygon } from "geojson";

export type QuantificationStatus = "ok" | "no_wind" | "failed";

/**
 * UI-side satellite filter key, normalized from the free-text `sensor` column
 * (see `normalizeSensor` in api.ts). ASSUMPTION: these four instruments; extend
 * if the real `sensor` values map to others.
 */
export type Satellite = "sentinel-2" | "landsat-8" | "landsat-9" | "emit";

/** public.plume — one detected methane plume. */
export interface Plume {
  plume_id: number;
  sensor: string;
  sensing_time: string;
  /** Plume footprint polygon (public.plume.geom). */
  geom: Polygon;
  /** source_point ?? centroid(geom); the emission locus, [lng, lat]. */
  locus: [number, number];
  area_m2: number | null;
  plume_length_m: number | null;
  max_likelihood: number;
  mean_likelihood: number | null;
  n_pixels: number;
  n_saturated_pixels: number;
  ime_mol: number | null;
  emission_rate_kg_hr: number | null;
  emission_rate_low: number | null;
  emission_rate_high: number | null;
  quantification_status: QuantificationStatus;
  quantification_variant: string;
  wind_speed_ms: number | null;
  wind_direction_deg: number | null;
  wind_source: string | null;
  wind_time: string | null;
  source_type: string | null;
}

/** map.episode_member — links a plume to its episode. */
export interface EpisodeMember {
  episode_id: number;
  plume_id: number;
}

/** map.episode — a cluster of plumes within `gap_days` of each other. */
export interface Episode {
  episode_id: number;
  gap_days: number;
  anchor_plume_id: number | null;
  first_seen: string;
  last_seen: string;
  duration_days: number;
  n_detections: number;
  n_dates: number;
  sensors: string[];
  max_rate_kg_hr: number | null;
  mean_rate_kg_hr: number | null;
  total_ch4_kg: number | null;
  /** Episode centroid, [lng, lat]. */
  centroid: [number, number];
  /** Convex hull over the episode's plumes. */
  hull: Polygon;
  extent_km: number;
  possibly_merged: boolean;
}

/** public.aoi — an area of interest (facility / potential source). */
export interface Aoi {
  aoi_id: number;
  name: string;
  /** Free-text category mapped to a sector label (see `sectorLabel`). */
  category: string;
  geom: Polygon;
  /** Centroid of `geom`, [lng, lat]. */
  centroid: [number, number];
}

/** The full dataset, shaped exactly like the four source tables. */
export interface Dataset {
  plumes: Plume[];
  members: EpisodeMember[];
  episodes: Episode[];
  aois: Aoi[];
}

// ── Derived UI concepts (not columns) ──────────────────────────────────────

/** Flow-rate grade (brief §6) — drives plume color and the legend. */
export type PlumeGrade = "low" | "moderate" | "high" | "severe" | "unknown";

export function gradeForRate(rate: number | null): PlumeGrade {
  if (rate === null) return "unknown";
  if (rate < 500) return "low";
  if (rate < 1000) return "moderate";
  if (rate < 2000) return "high";
  return "severe";
}

/** Grade rank for picking a source's worst plume (see api source coloring). */
export const GRADE_RANK: Record<PlumeGrade, number> = {
  unknown: 0,
  low: 1,
  moderate: 2,
  high: 3,
  severe: 4,
};
